import { uploadToS3 } from '@/utils/upload-to-s3';

import { apiAdminViewsFetch, handleApiResponseErrorWithoutToast } from '..';
import { _postUserUploadRequest } from '../user/mutations';
import type {
  CreateDealPipelineCardPayload,
  CreateDealPipelineInvoicePayload,
  DeleteDealPipelineCardPayload,
  EditDealPipelineNotePayload,
  InvestmentMoveData,
  MoveDealPipelineCardPayload,
  MoveInvestmentPipelineCardPayload,
  UpdateDealPipelineEntryPayload,
  UploadDealInvoiceFilePayload,
  WriteDealPipelineNotePayload,
} from './types';

export const _moveDealPipelineCard = ({
  cardId,
  targetStage,
  note,
  moneySentUsd,
  feesCollectedUsd,
  cancellationReason,
  cancellationOutcome,
  invoice,
}: MoveDealPipelineCardPayload) =>
  apiAdminViewsFetch<{ success: boolean }>(`/deal-board/entries/${cardId}/move`, {
    method: 'PATCH',
    body: {
      targetStage,
      ...(note !== undefined && note !== null ? { note } : {}),
      ...(moneySentUsd !== undefined ? { moneySentUsd } : {}),
      ...(feesCollectedUsd !== undefined ? { feesCollectedUsd } : {}),
      ...(cancellationReason !== undefined ? { cancellationReason } : {}),
      ...(cancellationOutcome !== undefined ? { cancellationOutcome } : {}),
      ...(invoice !== undefined ? { invoice } : {}),
    },
  });

export const _moveInvestmentPipelineCard = ({
  cardId,
  targetStage,
  note,
  investmentMoveData,
}: MoveInvestmentPipelineCardPayload) =>
  apiAdminViewsFetch<{ success: boolean }>(`/investment-board/entries/${cardId}/move`, {
    method: 'PATCH',
    query: { perspective: 'gamma' },
    body: getMoveBody(targetStage, note, undefined, investmentMoveData),
  });

/**
 * Partial edit of a deal entry's transaction data. `data` must carry only the
 * fields the user edited — the endpoint reads key presence as the instruction,
 * so a full object of nulls would erase everything.
 *
 * Returns only `{ success }`; re-read the entry detail to get the stored values.
 */
export const _updateDealPipelineEntry = ({ cardId, data }: UpdateDealPipelineEntryPayload) =>
  apiAdminViewsFetch<{ success: boolean }>(`/deal-board/entries/${cardId}`, {
    method: 'PATCH',
    body: data,
  });

/**
 * Creates a card by hand. Errors are handled by the create drawer (409 already exists, 422 offer
 * not open), so the global error toast is suppressed for this request.
 */
export const _createDealPipelineCard = (body: CreateDealPipelineCardPayload) =>
  apiAdminViewsFetch<{ id: string }>('/deal-board/entries', {
    method: 'POST',
    body,
    onResponseError: handleApiResponseErrorWithoutToast,
  });

/**
 * Uploads an invoice PDF and resolves to the file id the invoice row needs.
 *
 * The file service owns files by `user`/`entity`/`fund`/`thread` — there is no
 * deal or kanban owner — so the PDF is filed under the uploading Gamma member as
 * a document. The invoice row carries its own `created_by`, which is the audit
 * trail that actually matters here.
 *
 * `uploadToS3` does not resolve until the backend validator has confirmed the
 * upload, so a file id handed back from here is safe to attach to an invoice.
 */
export const _uploadDealInvoiceFile = async ({
  file,
  userId,
}: UploadDealInvoiceFilePayload): Promise<string> => {
  const request = await _postUserUploadRequest({
    userId,
    filename: file.name,
    contentType: file.type || 'application/pdf',
    category: 'document',
  });

  await uploadToS3({
    uploadUrl: request.uploadUrl,
    uploadHeaders: request.uploadHeaders,
    file,
    fileId: request.fileId,
  });

  return request.fileId;
};

/** Adds one invoice row. Re-read the entry afterwards to get the row and its id. */
export const _createDealPipelineInvoice = ({ cardId, ...body }: CreateDealPipelineInvoicePayload) =>
  apiAdminViewsFetch<{ success: boolean }>(`/deal-board/entries/${cardId}/invoices`, {
    method: 'POST',
    body,
  });

export const _createDealPipelineNote = ({ cardId, contentDoc }: WriteDealPipelineNotePayload) =>
  apiAdminViewsFetch<{ id: string }>(`/deal-board/entries/${cardId}/notes`, {
    method: 'POST',
    body: { contentDoc },
  });

/** Author only — nobody, including admins, can rewrite someone else's note. */
export const _updateDealPipelineNote = ({
  cardId,
  noteId,
  contentDoc,
}: EditDealPipelineNotePayload) =>
  apiAdminViewsFetch<{ success: boolean }>(`/deal-board/entries/${cardId}/notes/${noteId}`, {
    method: 'PATCH',
    body: { contentDoc },
  });

export const _deleteDealPipelineCard = ({ cardId }: DeleteDealPipelineCardPayload) =>
  apiAdminViewsFetch<{ success: boolean }>(`/deal-board/entries/${cardId}`, {
    method: 'DELETE',
  });

/**
 * `investmentMoveData` is spread in only when present: the backend rejects the
 * key outright on any stage other than `Invested`, so the deal board and every
 * other move keep sending a byte-identical body.
 */
const getMoveBody = (
  targetStage: string,
  note?: string | null,
  cancellationReason?: string,
  investmentMoveData?: InvestmentMoveData,
) => ({
  targetStage,
  ...(note === undefined || note === null ? {} : { note }),
  ...(cancellationReason === undefined ? {} : { cancellationReason }),
  ...(investmentMoveData === undefined ? {} : { investmentMoveData }),
});
